import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Snackbar, Alert, Paper, Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminGridToolbar from "../../components/admin/AdminGridToolbar";
import AdminFilterPanel, { filterFieldSx } from "../../components/admin/AdminFilterPanel";
import AdminFormDialog from "../../components/admin/AdminFormDialog";
import { listProcesses, createProcess, updateProcess, deleteProcess } from "../../services/adminService";

const GRID_SX = {
  border: 0,
  "& .MuiDataGrid-columnHeaders": { backgroundColor: "rgba(0,0,0,0.03)", borderBottom: "1px solid rgba(0,0,0,0.12)" },
  "& .MuiDataGrid-row":           { borderBottom: "1px solid rgba(0,0,0,0.06)" },
  "& .MuiDataGrid-cell":          { outline: "none" },
};

const EMPTY = { process: "", description_process: "" };

export default function ProcessosPage() {
  const { t } = useTranslation();
  const [rows, setRows]               = useState([]);
  const [search, setSearch]           = useState("");
  const [loading, setLoading]         = useState(false);
  const [rowSelectionModel, setRowSel]= useState({ type: "include", ids: new Set() });
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [deleteOpen, setDeleteOpen]   = useState(false);
  const [editing, setEditing]         = useState(null);
  const [form, setForm]               = useState(EMPTY);
  const [saving, setSaving]           = useState(false);
  const [snack, setSnack]             = useState({ open: false, msg: "", severity: "success" });

  const selectedIds = useMemo(() => Array.from(rowSelectionModel.ids), [rowSelectionModel]);
  const selectedRow = useMemo(() => rows.find(r => r.process === selectedIds[0]), [rows, selectedIds]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await listProcesses()); }
    catch (e) { showSnack(e.message, "error"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      r.process?.toLowerCase().includes(q) ||
      r.description_process?.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const showSnack   = (msg, severity = "success") => setSnack({ open: true, msg, severity });
  const openNew     = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit    = () => { if (!selectedRow) return; setEditing(selectedRow); setForm({ ...selectedRow }); setDialogOpen(true); };
  const closeDialog = () => setDialogOpen(false);

  const handleSave = async () => {
    if (!form.process.trim()) return showSnack(t("admin.required.processCode"), "error");
    setSaving(true);
    try {
      if (editing) await updateProcess(editing.process, { description_process: form.description_process });
      else         await createProcess(form);
      showSnack(editing ? t("admin.success.processUpdated") : t("admin.success.processCreated"));
      closeDialog();
      load();
    } catch (e) { showSnack(e.message, "error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await Promise.all(selectedIds.map(id => deleteProcess(id)));
      showSnack(t("admin.success.processDeleted"));
      setDeleteOpen(false);
      setRowSel({ type: "include", ids: new Set() });
      load();
    } catch (e) { showSnack(e.message, "error"); }
    finally { setSaving(false); }
  };

  const columns = [
    { field: "process",             headerName: t("admin.col.codigo"),   width: 160 },
    { field: "description_process", headerName: t("admin.col.descricao"), flex: 1 },
  ];

  const f = (field) => ({ value: form[field] ?? "", onChange: e => setForm(p => ({ ...p, [field]: e.target.value })) });

  return (
    <Box sx={{ p: 2 }}>
      <AdminPageHeader title={t("admin.pages.processos")} />

      <AdminFilterPanel>
        <TextField label="Código / Descrição" size="small"
          value={search} onChange={e => setSearch(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ ...filterFieldSx, width: 280 }} />
      </AdminFilterPanel>

      <Paper variant="outlined">
        <Divider />
        <DataGrid
          rows={filtered} columns={columns} getRowId={r => r.process}
          loading={loading} autoHeight checkboxSelection disableRowSelectionOnClick
          density="compact" showToolbar
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={setRowSel}
          slots={{ toolbar: AdminGridToolbar }}
          slotProps={{ toolbar: {
            title: t("admin.grid.processos"),
            onNew: openNew, onEdit: openEdit, onDelete: () => setDeleteOpen(true),
            canEdit: selectedIds.length === 1, canDelete: selectedIds.length > 0,
          }}}
          sx={GRID_SX}
        />
      </Paper>

      <AdminFormDialog
        open={dialogOpen} onClose={closeDialog} onSave={handleSave} saving={saving}
        title={editing ? t("admin.dialog.editProcesso") : t("admin.dialog.newProcesso")}
      >
        <TextField label="Código *" {...f("process")} disabled={!!editing} size="small"
          InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 10 }} />
        <TextField label="Descrição" {...f("description_process")} size="small"
          InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 255 }} />
      </AdminFormDialog>

      <Dialog open={deleteOpen} onClose={() => !saving && setDeleteOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: 14, pb: 1, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>{t("admin.dialog.confirmDelete")}</DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.6 }}>
            {t("admin.confirm.deleteProcesso", { count: selectedIds.length })}
          </Typography>
        </DialogContent>
        <Divider sx={{ opacity: 0.5 }} />
        <DialogActions sx={{ px: 2, py: 1.2, gap: 1 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving} sx={{ textTransform: "none", fontSize: 13 }}>{t("admin.buttons.cancel")}</Button>
          <Button onClick={handleDelete} disabled={saving} disableElevation sx={{ textTransform: "none", fontSize: 13, color: "#d32f2f", backgroundColor: "rgba(211,47,47,0.10)", "&:hover": { backgroundColor: "rgba(211,47,47,0.18)" }, borderRadius: 0 }}>
            {saving ? t("admin.buttons.deleting") : t("admin.buttons.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(p => ({ ...p, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(p => ({ ...p, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
